/**
 * Date formatting utilities for event dates
 * 
 * Handles:
 * - Date range display (start_date to end_date)
 * - Month-only display (event_month in YYYY-MM format)
 * - Display override (custom text like "Spring 2026")
 * - Internal vs proposal page display logic
 * 
 * IMPORTANT: Date-only strings (YYYY-MM-DD) are parsed as local calendar dates,
 * not UTC, to avoid timezone-related off-by-one errors (e.g. March 16 showing as March 15
 * for users in Americas).
 */

/**
 * Parses a date-only string (YYYY-MM-DD or ISO with time) as local calendar date.
 * Avoids new Date("YYYY-MM-DD") which interprets as UTC midnight and causes
 * off-by-one display errors for users west of UTC.
 */
export function parseDateOnly(dateString: string): Date | null {
  if (!dateString || typeof dateString !== 'string') return null;
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a date string for HTML date input (YYYY-MM-DD).
 * Uses parseDateOnly to avoid timezone shifts.
 */
export function formatDateForInput(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = parseDateOnly(dateString);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a month string from YYYY-MM to display format
 * @param monthString - Format: "YYYY-MM" (e.g., "2026-05")
 * @returns Formatted string (e.g., "May 2026")
 */
export function formatMonth(monthString: string): string {
  if (!monthString) return '';
  
  try {
    const [year, month] = monthString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  } catch {
    return monthString; // Fallback to raw value
  }
}

/**
 * Formats a date range for display
 * @param startDate - ISO date string or null
 * @param endDate - ISO date string or null
 * @returns Formatted range (e.g., "Oct 12 - Oct 16, 2026")
 */
export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  if (!startDate && !endDate) return '';
  
  const formatDate = (dateString: string): { month: string; day: string; year: string } => {
    const date = parseDateOnly(dateString);
    if (!date) return { month: '', day: '', year: '' };
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.toLocaleDateString('en-US', { day: 'numeric' }),
      year: date.toLocaleDateString('en-US', { year: 'numeric' }),
    };
  };
  
  if (startDate && endDate) {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (start.year === end.year) {
      return `${start.month} ${start.day} - ${end.month} ${end.day}, ${start.year}`;
    }
    return `${start.month} ${start.day}, ${start.year} - ${end.month} ${end.day}, ${end.year}`;
  } else if (startDate) {
    const start = formatDate(startDate);
    return `From ${start.month} ${start.day}, ${start.year}`;
  } else if (endDate) {
    const end = formatDate(endDate);
    return `Until ${end.month} ${end.day}, ${end.year}`;
  }
  
  return '';
}

/**
 * Event date fields interface
 */
export interface EventDateFields {
  date_type?: 'date_range' | 'month' | null;
  event_month?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  date_display_override?: string | null;
}

/**
 * Formats event dates for display, respecting date_type and override
 * @param event - Event object with date fields
 * @param isProposal - If true (proposal page), use override only. If false (internal), show both.
 * @returns Formatted date string
 */
export function formatEventDates(
  event: EventDateFields,
  isProposal: boolean = false
): string {
  // For proposal pages: use override if available
  if (isProposal && event.date_display_override) {
    return event.date_display_override;
  }
  
  // Get actual date display
  let actualDisplay = '';
  
  if (event.date_type === 'month' && event.event_month) {
    actualDisplay = formatMonth(event.event_month);
  } else {
    actualDisplay = formatDateRange(event.start_date, event.end_date);
  }
  
  // For internal pages: show "Override (Actual)" format
  if (!isProposal && event.date_display_override && actualDisplay) {
    return `${event.date_display_override} (${actualDisplay})`;
  }
  
  // If no actual display but has override (edge case), show override
  if (!actualDisplay && event.date_display_override) {
    return event.date_display_override;
  }
  
  return actualDisplay;
}

/**
 * Gets travel month for weather research (never uses override)
 * @param event - Event object with date fields
 * @returns Month name (e.g., "May" or "May-June")
 */
export function getTravelMonthForWeather(event: EventDateFields): string {
  // If date_type is 'month', parse and return month name
  if (event.date_type === 'month' && event.event_month) {
    try {
      const [year, month] = event.event_month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleString('en-US', { month: 'long' });
    } catch {
      return 'May'; // Fallback
    }
  }
  
  // Otherwise calculate from date range
  if (!event.start_date) return 'May'; // Default fallback
  
  const startDate = parseDateOnly(event.start_date);
  const endDate = event.end_date ? parseDateOnly(event.end_date) : startDate;
  if (!startDate) return 'May';
  
  const startMonth = startDate.toLocaleString('en-US', { month: 'long' });
  const endMonth = endDate ? endDate.toLocaleString('en-US', { month: 'long' }) : startMonth;
  
  if (startMonth === endMonth) {
    return startMonth;
  }
  
  return `${startMonth}-${endMonth}`;
}

